import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Mail, 
  BookOpen, 
  Users, 
  Check, 
  ArrowRight, 
  Search, 
  MessageCircle, 
  Globe,
  TrendingUp,
  Star,
  ChevronRight,
  Sparkles,
  FileText,
  Award,
  Zap
} from 'lucide-react';

export function Home() {
  const navigate = useNavigate();

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.1,
        duration: 0.5
      }
    })
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-purple-900/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-blue-900/20 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative overflow-hidden min-h-[95vh] flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="pt-8 pb-12 md:pt-16 md:pb-20 lg:pt-20 lg:pb-24">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center justify-center p-2 bg-indigo-800/30 backdrop-blur-sm rounded-full mb-6 border border-indigo-700/50"
              >
                <span className="px-3 py-0.5 text-sm font-medium text-indigo-300">
                  Research made simple for students
                </span>
              </motion.div>

              <motion.h1 
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400">
                  Connect with Leading
                </span>
                <span className="block text-white mt-2">
                  Research Professors
                </span>
              </motion.h1>

              <motion.p 
                className="mt-6 max-w-2xl mx-auto text-xl text-gray-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Bridge the gap between high school students and university professors. 
                Start your research journey with AI-powered professor matching and email generation.
              </motion.p>

              <motion.div 
                className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <motion.button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg text-base font-medium shadow-lg shadow-indigo-900/30 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </motion.button>
                <motion.button
                  onClick={() => navigate('/login')} 
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg text-base font-medium border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Sign In
                </motion.button>
              </motion.div>
            </div>
          </div>
          
          <motion.div 
            className="relative z-10 max-w-5xl mx-auto mt-16 sm:mt-10 md:mt-0 md:-mt-12 mb-20" 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {/* Uncomment this section to display statistics
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-8">
              {[
                { number: "10,000+", label: "Professors" },
                { number: "95%", label: "Response Rate" },
                { number: "500+", label: "Universities" },
                { number: "1,200+", label: "Students" }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-5 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + (index * 0.1) }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <p className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                    {stat.number}
                  </p>
                  <p className="text-gray-400 text-xs sm:text-sm mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
            */}
          </motion.div>
        </div>
      </div>
      <div className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-extrabold sm:text-4xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400"
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUpVariants}
            >
              How Research-X Works
            </motion.h2>
            <motion.p 
              className="mt-4 max-w-2xl mx-auto text-lg text-gray-300"
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUpVariants}
            >
              Get connected with professors in just three simple steps
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <Search className="h-8 w-8 text-indigo-400" />,
                title: "Find Professors",
                description: "Search our database of 10,000+ professors based on research interests, university, or department."
              },
              {
                icon: <Mail className="h-8 w-8 text-indigo-400" />,
                title: "Contact With AI",
                description: "Use our AI to generate personalized emails tailored to each professor's research focus."
              },
              {
                icon: <MessageCircle className="h-8 w-8 text-indigo-400" />,
                title: "Track Responses",
                description: "Monitor your progress and manage professor responses all in one dashboard."
              }
            ].map((step, index) => (
              <motion.div 
                key={index}
                className="bg-gray-900/50 rounded-lg border border-gray-700 p-8 relative"
                custom={index + 2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUpVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
                  <span className="text-xl font-bold text-indigo-400">{index + 1}</span>
                </div>
                <div className="h-14 w-14 rounded-lg bg-indigo-900/30 flex items-center justify-center mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h2 
              className="text-3xl font-extrabold sm:text-4xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400"
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUpVariants}
            >
              Powerful Features for Researchers
            </motion.h2>
            <motion.p 
              className="mt-4 max-w-2xl mx-auto text-lg text-gray-300"
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUpVariants}
            >
              Everything you need to connect with leading researchers and advance your academic journey
            </motion.p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: <Search className="h-6 w-6 text-indigo-400" />,
                title: "Professor Search",
                description: "Find professors based on research interests, university, or department with our comprehensive database."
              },
              {
                icon: <Sparkles className="h-6 w-6 text-indigo-400" />,
                title: "AI Email Generator",
                description: "Create professional, personalized emails with our AI technology that understands research contexts."
              },
              {
                icon: <FileText className="h-6 w-6 text-indigo-400" />,
                title: "Email Templates",
                description: "Save and reuse custom email templates to streamline your outreach process."
              },
              {
                icon: <TrendingUp className="h-6 w-6 text-indigo-400" />,
                title: "Response Analytics",
                description: "Track response rates and analyze patterns to improve your outreach strategy."
              },
              {
                icon: <Users className="h-6 w-6 text-indigo-400" />,
                title: "Research Community",
                description: "Connect with other student researchers and share experiences and opportunities."
              },
              {
                icon: <Award className="h-6 w-6 text-indigo-400" />,
                title: "Portfolio Builder",
                description: "Create a professional research portfolio to showcase your work and achievements."
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-800 rounded-xl border border-gray-700 p-6"
                custom={index + 2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUpVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="h-12 w-12 rounded-lg bg-indigo-900/30 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      {/* Testimonials section - to be added later
      <div className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUpVariants}
          >
            <h2 className="text-3xl font-extrabold sm:text-4xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
              Success Stories
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-300">
              Hear from students who found research opportunities with Research-X
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                quote: "Research-X helped me connect with a professor at MIT for my machine learning project. The AI-generated email was perfectly tailored to their research interests.",
                name: "Alex Johnson",
                role: "High School Senior",
                university: "Accepted to Stanford University"
              },
              {
                quote: "I was struggling to find professors in my niche field. Research-X not only found relevant professors but also helped me craft emails that got responses.",
                name: "Maya Patel",
                role: "Undergraduate Researcher",
                university: "Cornell University"
              },
              {
                quote: "The response tracking feature is amazing. I went from a 20% to 75% response rate by following the analytics insights.",
                name: "Ethan Williams",
                role: "Research Assistant",
                university: "UC Berkeley"
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                className="bg-gray-900 rounded-xl border border-gray-700 p-6 relative"
                custom={index + 1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUpVariants}
              >
                <div className="absolute -top-3 -left-3">
                  <Star className="h-6 w-6 text-yellow-400" fill="#FBBF24" />
                </div>
                <p className="text-gray-300 italic mb-6">{testimonial.quote}</p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-gray-400 text-sm">{testimonial.role}</p>
                  <p className="text-indigo-400 text-sm">{testimonial.university}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      */}
      {/* Simplified Pricing section - Single Tier */}
      <div className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUpVariants}
          >
            <h2 className="text-3xl font-extrabold sm:text-4xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-300">
              Everything you need to accelerate your research journey
            </p>
          </motion.div>

          <motion.div 
            className="max-w-lg mx-auto"
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUpVariants}
          >
            {/* Basic Plan */}
            <motion.div 
              className="bg-gray-800 rounded-xl border-2 border-indigo-500 p-8 relative shadow-xl shadow-indigo-900/20"
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              custom={2}
              variants={fadeInUpVariants}
            >
              <h3 className="text-2xl font-bold text-white flex items-center">
                <Zap className="h-6 w-6 text-indigo-400 mr-3" />
                Basic Plan
              </h3>
              <p className="mt-3 text-gray-400">
                Perfect for students starting their research journey
              </p>
              
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                  $9.99
                </span>
                <span className="text-gray-400 text-sm ml-2">/month</span>
              </div>
              
              <div className="mt-6 space-y-4">
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">
                    <span className="font-medium text-white">20 professor searches</span> per week
                  </span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">
                    <span className="font-medium text-white">AI-powered email generation</span> personalized to each professor
                  </span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">
                    <span className="font-medium text-white">Track up to 25 professors</span> and manage all communications
                  </span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">
                    <span className="font-medium text-white">Email analytics</span> to improve your response rates
                  </span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">
                    <span className="font-medium text-white">Standard email templates</span> for different research scenarios
                  </span>
                </div>
              </div>
              
              <motion.button
                onClick={() => navigate('/pricing')}
                className="mt-8 w-full py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 rounded-lg text-base font-medium text-white shadow-lg shadow-indigo-900/20"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started
              </motion.button>
            </motion.div>
          </motion.div>

          {/* View all details button */}
          <motion.div 
            className="mt-12 text-center"
            custom={5}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUpVariants}
          >
            <motion.button
              onClick={() => navigate('/pricing')}
              className="inline-flex items-center px-6 py-3 border border-gray-700 rounded-lg text-base font-medium text-white hover:bg-gray-800 transition-colors"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              View Full Plan Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </motion.button>
          </motion.div>
        </div>
      </div>

      <div className="py-16 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-800 to-purple-800 opacity-90"></div>
            
            <div className="relative px-8 py-16 md:px-12 md:py-20 text-center">
              <motion.h2 
                className="text-3xl font-extrabold text-white sm:text-4xl"
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUpVariants}
              >
                Ready to Start Your Research Journey?
              </motion.h2>
              <motion.p 
                className="mt-4 max-w-2xl mx-auto text-lg text-indigo-100"
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUpVariants}
              >
                Join thousands of students who've found research opportunities with Research-X
              </motion.p>
              <motion.div 
                className="mt-10"
                custom={2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUpVariants}
              >
                <motion.button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg text-base font-medium shadow-xl bg-white text-indigo-700 hover:bg-gray-100"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Started
                  <ChevronRight className="ml-2 h-5 w-5" />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}